import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Toast } from "primereact/toast";

import { useStore } from "../../../../stores/StoreContext";
import { IToast } from "../../../../stores/ToastStore";

const CallableToastRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { toastStore } = useStore();

  const [toastData, setToastData] = useState<IToast>();

  const toastRef = useRef<Toast>(null);
  const showToastTimeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const showToast = (toast: IToast) => {
      let delay = toast.delay ?? 0;

      if (toastData?.replace) {
        toastRef.current?.clear();
        delay = 1000;
      }

      // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout -- id is tracked in the Set and cleared for all pending timeouts on unmount below
      const timeoutId = setTimeout(() => {
        showToastTimeoutIdsRef.current.delete(timeoutId);

        toastRef.current?.show({
          severity: toast.severity,
          summary: toast.summary.translationKey ? t(toast.summary.translationKey) : toast.summary.message,
          detail: toast.detail.translationKey
            ? t(toast.detail.translationKey, toast.detail.translationParams)
            : toast.detail.message,
          life: toast.life ?? 5000,
          sticky: toast.sticky,
        });
        setToastData(toast);
      }, delay);

      showToastTimeoutIdsRef.current.add(timeoutId);
    };

    if (toastStore.toastCount > 0) {
      const toast = toastStore.processNextToast();
      if (toast) {
        showToast(toast);
      }
    }
  }, [toastStore.toastCount]);

  // Clear any pending show-toast timeouts only on unmount, not on every toastCount change,
  // since processNextToast() above mutates toastCount and would otherwise cancel itself
  useEffect(() => {
    const timeoutIds = showToastTimeoutIdsRef.current;

    return () => timeoutIds.forEach(clearTimeout);
  }, []);

  return <Toast ref={toastRef} position={toastData?.position ?? "top-right"} />;
};

export const CallableToast = observer(CallableToastRaw);
